import { DataProvider } from '@repo/ui/dataProvider'
import { cn } from '@repo/ui/utils'
import { PanelLeftOpen } from 'lucide-react'
import { memo, ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAbility } from '@/ability/ability-context.ts'
import Pattern from '@/assets/pattern.svg'
import { ProcessingStatus } from '@/components/admin/structure-data/ProcessingStatus'
import { Logo } from '@/components/layouts/Logo'
import { Panel, PanelStatus } from '@/components/layouts/panels/Panel'
import { MeMenu } from '@/components/MeMenu'
import { useView } from '@/hooks'
import { RootState } from '@/store'
import { getCurrentOrganization } from '@/store/helpers'

export const leftSize = 320
export const mainLeftOffset = 50
const leftOffset = -(leftSize - mainLeftOffset)

export const LeftPanel = memo(function ({
  children,
  status,
  className,
}: {
  children: ReactNode
  status: PanelStatus
  className?: string
}) {
  if (status === 'floating') return <AnimatedPanel>{children}</AnimatedPanel>
  if (status === 'fixed')
    return (
      <div
        className={cn(
          'relative flex max-w-64 flex-1 overflow-y-auto 2xl:w-80 2xl:min-w-80',
          className,
        )}
      >
        <PanelContent className="rounded-lg">{children}</PanelContent>
      </div>
    )
  return null
})

function AnimatedPanel({ children }: { children?: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    setIsOpen(false)
  }, [])
  const closePanel = () => setIsOpen(false)
  return (
    <Panel offset={leftOffset} width={leftSize} isOpen={isOpen} setIsOpen={setIsOpen}>
      <PanelContent
        rightSlot={<LeftPanelIcon closePanel={closePanel} />}
        className="rounded-r-lg"
        onTouchMove={closePanel}
      >
        {children}
      </PanelContent>
    </Panel>
  )
}

function PanelContent({
  className,
  children,
  rightSlot,
  onTouchMove,
}: {
  className?: string
  children: ReactNode
  rightSlot?: ReactNode
  onTouchMove?: () => void
}) {
  return (
    <div className="flex h-full w-full">
      <div
        className={cn('bg-primary-950 relative flex h-full w-full', className)}
        onTouchMove={onTouchMove}
      >
        <BgImage />
        <div className="z-10 flex h-full flex-1 select-none flex-col items-start">
          <Header />
          <div className="text-primary-foreground flex w-full flex-1 flex-col overflow-hidden">
            {children}
          </div>
          <Footer />
        </div>
      </div>
      {rightSlot}
    </div>
  )
}

function LeftPanelIcon({ closePanel }: { closePanel: () => void }) {
  return (
    <div
      className="bg-primary-500/20 my-6 flex w-8 flex-shrink-0 items-center justify-center rounded-r-lg pt-6"
      onTouchMove={closePanel}
    >
      <PanelLeftOpen className="text-primary-400" />
    </div>
  )
}

function Header() {
  const view = useView()
  return (
    <div className={cn('mx-8 my-6 flex flex-col gap-4')}>
      <Link to={view.dashboard().route}>
        <Logo isWhite />
      </Link>
    </div>
  )
}

function Footer() {
  const ability = useAbility()
  return (
    <div className="flex w-full flex-shrink-0 flex-col gap-3 p-4">
      <DataProvider
        selectors={[
          (state: RootState) => state.user.me,
          (state: RootState) => state.user.caseWorker,
          getCurrentOrganization,
        ]}
        render={(user, caseWorker, organization) => (
          <>
            {ability.can('manage', 'RefreshAllStructuredData') && <ProcessingStatus />}

            <MeMenu
              user={user}
              caseWorker={caseWorker}
              organization={organization}
              display="full"
            />
          </>
        )}
      />
    </div>
  )
}

export function BgImage() {
  return (
    <div
      className="absolute inset-0 bg-cover bg-center opacity-10"
      style={{ backgroundImage: `url(${Pattern})` }}
    />
  )
}
